// Feste Themen-Liste – wird überall im Frontend verwendet.
// Neue Themen werden über die Themen-Verwaltungsseite hinzugefügt,
// indem man bestehende Piktogramme einem neuen Thema zuweist.

export const DEFAULT_CATEGORY = 'allgemein';

/** Themen, die nicht gelöscht werden dürfen */
export const PROTECTED_CATEGORIES = new Set(['allgemein', 'kommunikation', 'kernvokabular']);

export const CATEGORIES: { value: string; label: string }[] = [
  { value: 'allgemein', label: 'Allgemein' },
  { value: 'kommunikation', label: 'Kommunikation' },
  { value: 'kernvokabular', label: 'Kernvokabular' },
  { value: 'alltag', label: 'Alltag' },
  { value: 'essen', label: 'Essen' },
  { value: 'gefuehle', label: 'Gefühle' },
  { value: 'aktivitaeten', label: 'Aktivitäten' },
  { value: 'transport', label: 'Transport' },
  { value: 'personen', label: 'Personen' },
  { value: 'orte', label: 'Orte' },
  { value: 'zeit', label: 'Zeit' },
  { value: 'koerper', label: 'Körper' },
  { value: 'gesundheit', label: 'Gesundheit' },
];

/** Alle Themen-Werte als Array */
export const CATEGORY_VALUES = CATEGORIES.map((c) => c.value);

/** Label für einen Themen-Wert */
export const categoryLabel = (value: string) =>
  CATEGORIES.find((c) => c.value === value)?.label ?? value;

/**
 * Fitzgerald-Key Farbcodierung für Wortkategorien.
 * Gelb = Personen/Pronomen, Grün = Verben/Aktivitäten, Blau = Adjektive/Gefühle,
 * Orange = Substantive/Dinge, Rosa = Soziale Ausdrücke/Kernvokabular, Grau = Allgemein.
 */

export interface FitzgeraldColor {
  border: string;
  bg: string;
  pdfColor: [number, number, number];
  label: string;
}

/** Auswählbare Fitzgerald-Farbgruppen */
export const FITZGERALD_OPTIONS: { key: string; color: FitzgeraldColor }[] = [
  { key: 'gelb', color: { border: 'border-yellow-400', bg: 'bg-yellow-400', pdfColor: [234, 179, 8], label: 'Gelb – Personen/Pronomen' } },
  { key: 'gruen', color: { border: 'border-green-500', bg: 'bg-green-500', pdfColor: [34, 197, 94], label: 'Grün – Verben/Aktivitäten' } },
  { key: 'blau', color: { border: 'border-blue-400', bg: 'bg-blue-400', pdfColor: [96, 165, 250], label: 'Blau – Gefühle/Adjektive' } },
  { key: 'orange', color: { border: 'border-orange-400', bg: 'bg-orange-400', pdfColor: [251, 146, 60], label: 'Orange – Dinge/Substantive' } },
  { key: 'rosa', color: { border: 'border-pink-400', bg: 'bg-pink-400', pdfColor: [244, 114, 182], label: 'Rosa – Kommunikation/Kernvokabular' } },
  { key: 'grau', color: { border: 'border-gray-300', bg: 'bg-gray-300', pdfColor: [209, 213, 219], label: 'Grau – Allgemein' } },
];

const _defaultMap: Record<string, FitzgeraldColor> = {
  personen: FITZGERALD_OPTIONS[0]!.color,
  aktivitaeten: FITZGERALD_OPTIONS[1]!.color,
  gefuehle: FITZGERALD_OPTIONS[2]!.color,
  kommunikation: FITZGERALD_OPTIONS[4]!.color,
  kernvokabular: FITZGERALD_OPTIONS[4]!.color,
  essen: FITZGERALD_OPTIONS[3]!.color,
  alltag: FITZGERALD_OPTIONS[3]!.color,
  transport: FITZGERALD_OPTIONS[3]!.color,
  orte: FITZGERALD_OPTIONS[3]!.color,
  zeit: FITZGERALD_OPTIONS[3]!.color,
  koerper: FITZGERALD_OPTIONS[3]!.color,
  gesundheit: FITZGERALD_OPTIONS[3]!.color,
  allgemein: FITZGERALD_OPTIONS[5]!.color,
};

/** Runtime-erweiterbare Fitzgerald-Zuordnung (Fallback für Piktogramme ohne wordClass) */
export const FITZGERALD_COLORS: Record<string, FitzgeraldColor> = { ..._defaultMap };

/** Fitzgerald-Farbe für ein Thema registrieren */
export function registerFitzgeraldColor(categoryValue: string, optionKey: string) {
  const opt = FITZGERALD_OPTIONS.find((o) => o.key === optionKey);
  if (opt) FITZGERALD_COLORS[categoryValue] = opt.color;
}

// ── Wortklasse → Fitzgerald (UK-Standard: Fitzgerald-Schlüssel basiert auf Wortklasse, nicht Thema) ──

export type WordClass = 'nomen' | 'verb' | 'adjektiv' | 'person' | 'sozial' | 'sonstig';

export const WORD_CLASS_OPTIONS: { value: WordClass; label: string; fitzgerald: string }[] = [
  { value: 'person', label: 'Person/Pronomen', fitzgerald: 'gelb' },
  { value: 'verb', label: 'Verb/Tätigkeit', fitzgerald: 'gruen' },
  { value: 'adjektiv', label: 'Adjektiv/Beschreibend', fitzgerald: 'blau' },
  { value: 'nomen', label: 'Substantiv/Ding', fitzgerald: 'orange' },
  { value: 'sozial', label: 'Sozial/Kernvokabular', fitzgerald: 'rosa' },
  { value: 'sonstig', label: 'Sonstiges', fitzgerald: 'grau' },
];

const WORDCLASS_FITZGERALD: Record<string, FitzgeraldColor> = {
  person: FITZGERALD_OPTIONS[0]!.color,
  verb: FITZGERALD_OPTIONS[1]!.color,
  adjektiv: FITZGERALD_OPTIONS[2]!.color,
  nomen: FITZGERALD_OPTIONS[3]!.color,
  sozial: FITZGERALD_OPTIONS[4]!.color,
  sonstig: FITZGERALD_OPTIONS[5]!.color,
};

/** Fitzgerald-Farbe: wordClass hat Vorrang, Fallback auf category (Rückwärtskompatibilität) */
export function getFitzgeraldColor(pic: { wordClass?: string; category?: string }): FitzgeraldColor {
  if (pic.wordClass && WORDCLASS_FITZGERALD[pic.wordClass]) {
    return WORDCLASS_FITZGERALD[pic.wordClass]!;
  }
  return FITZGERALD_COLORS[pic.category ?? ''] ?? FITZGERALD_OPTIONS[5]!.color;
}

/** Vorgeschlagene Wortklasse basierend auf Thema (Hilfe für Nutzer beim Upload) */
export function suggestWordClass(category: string): WordClass {
  switch (category) {
    case 'personen': return 'person';
    case 'aktivitaeten': return 'verb';
    case 'gefuehle': return 'adjektiv';
    case 'kommunikation': case 'kernvokabular': return 'sozial';
    case 'essen': case 'alltag': case 'transport': case 'orte': case 'zeit': case 'koerper': case 'gesundheit': return 'nomen';
    default: return 'sonstig';
  }
}
